import { useState } from "react";
import type { PermissionCode } from "@koclukpanel/authz";
import { BaglamSeridi, BosDurum, Buton, SekmePaneli } from "@koclukpanel/ui";
import { GORUNEN_SEKME_ADEDI, OGRENCI_SEKMELERI, SEKME_SIRASI } from "./sekmeler";

/**
 * ÖĞRENCİ 360° KABUĞU — F-PPL-04
 *
 * ================================================================
 * NEDEN BU EKRAN VAR
 * ================================================================
 * Panelde bugün ON BİR ayrı menü girdisi "önce öğrenciyi seç" diyor:
 * program · konu ilerlemesi · konu planı · öğrenci kaynakları ·
 * denemeler · koçluk görüşmeleri · veli raporları · hata defteri ·
 * haftalık analiz · abonelik · öğrenci dosyası. Hepsi aynı öğrenciye
 * bakıyor ve her biri seçimi yeniden sordurabiliyor.
 *
 * Bu ekran seçimi BİR KEZ soruyor ve gerisini sekmeye çeviriyor.
 *
 * ================================================================
 * SEKMEYİ KABUK SEÇMİYOR — KAYIT DEFTERİ SEÇİYOR
 * ================================================================
 * Kabuk hangi sekmelerin var olduğunu BİLMİYOR: `sekmeler.tsx`
 * listesini alıyor, izne göre süzüyor, çiziyor. Yeni sekme eklemek
 * tek satır; kabukta hiçbir şey değişmiyor.
 *
 * Süzgeç bir KORUMA değil (gerekçe `sekmeler.tsx` başlığında):
 * verinin korunması sunucuda, her ucun kendi izninde ve kendi kayıt
 * bazlı kapsam kontrolünde.
 */
export function Ogrenci360({
  ogrenciId,
  ogrenciAdi,
  izinler,
  onOgrenciDegistir,
}: {
  ogrenciId: string;
  ogrenciAdi: string;
  /** `/auth/ben` izin haritası — ham hâliyle geliyor, tipi burada daralıyor */
  izinler: Record<string, string>;
  onOgrenciDegistir: () => void;
}) {
  /*
    Ham harita `Record<string, string>`; sekmeler `PermissionCode`
    soruyor. Daraltma TEK YERDE oluyor: sekmelerin içinde ham dizgi
    hiç dolaşmıyor, dolayısıyla yazım hatası derlenmiyor.
  */
  const izinVar = (kod: PermissionCode): boolean => kod in izinler;

  /*
    Sira KAYIT DEFTERINDEN degil `SEKME_SIRASI`ndan: ilk bes seritte, kalani "Diger"
    menusunde (UI/UX Dalga 2, D4). Listede olmayan sekme sona dusuyor — kaybolmuyor.
  */
  const siraNo = (anahtar: string): number => {
    const i = SEKME_SIRASI.indexOf(anahtar);
    return i === -1 ? SEKME_SIRASI.length : i;
  };
  const gorunen = OGRENCI_SEKMELERI.filter((s) => s.izin.some(izinVar)).sort(
    (a, b) => siraNo(a.anahtar) - siraNo(b.anahtar),
  );

  const [secilen, setSecilen] = useState<string>(() => gorunen[0]?.anahtar ?? "");

  /*
    ================================================================
    SEÇİLİ SEKME KAYBOLABİLİR — VE BU GERÇEK BİR SENARYO
    ================================================================
    Panelde "kullanıcı görünümü" (`sistem.gorunum.gec`) var: yönetici
    başka bir kullanıcının gözünden bakabiliyor ve o an izin haritası
    OTURUM ORTASINDA değişiyor. Açık sekme o haritada yoksa kabuk
    boş bir panel çizerdi — hiçbir hata vermeden.

    `useState` başlangıç değeri yalnızca ilk çizimde okunuyor, yani
    bunu orada çözmek mümkün değil. Çizim anında doğrulanıyor.
  */
  const aktif = gorunen.find((s) => s.anahtar === secilen) ?? gorunen[0];

  if (!aktif) {
    /*
      Buraya normalde gelinmiyor: "Genel Bakış" `kisi.ogrenci.goruntule`
      istiyor ve o izin olmadan öğrenci zaten seçilemezdi. Yine de sessiz
      boş bir ekran bırakmıyoruz — gelinirse sebebi görünsün.
    */
    return (
      <BosDurum
        baslik="Görüntülenecek bölüm yok"
        aciklama="Bu öğrenci için yetkiniz dahilinde açılabilecek bir bölüm bulunamadı."
      />
    );
  }

  const { Icerik } = aktif;

  return (
    <div className="flex flex-col gap-4">
      {/*
        UNİKOÇ öğrenci detay penceresinin başlık şeridi (tarama-2 §3.8): lavanta → pembe
        degrade, yuvarlak avatar + ad. Sol çizgi kaldırıldı: şerit degrade zeminde zaten ayrışıyor.
      */}
      <BaglamSeridi
        className="rounded-[1.5rem] border-l-0 bg-(image:--gradyan-unk-bant) shadow-(--golge-unk-kart)"
        basHarfler={basHarfler(ogrenciAdi)}
        ad={ogrenciAdi}
        eylem={
          <Buton gorunum="ikincil" onClick={onOgrenciDegistir}>
            Öğrenci değiştir
          </Buton>
        }
      />

      <SekmePaneli
        sekmeler={gorunen.map((s) => ({ deger: s.anahtar, etiket: s.etiket }))}
        secili={aktif.anahtar}
        onSec={setSecilen}
        gorunenAdet={GORUNEN_SEKME_ADEDI}
        etiket="Öğrenci bölümleri"
        kimlikOneki="ogrenci360"
      >
        {/*
          `key`: sekme değiştiğinde içerik bileşeni SIFIRDAN kuruluyor.
          Olmasaydı React aynı konumdaki iki farklı ekranı eşleştirmeye
          çalışır ve önceki sekmenin iç durumu (açık form, seçili satır)
          yeni sekmeye sızardı.
        */}
        <Icerik
          key={aktif.anahtar}
          ogrenciId={ogrenciId}
          ogrenciAdi={ogrenciAdi}
          izinVar={izinVar}
          onOgrenciDegistir={onOgrenciDegistir}
        />
      </SekmePaneli>
    </div>
  );
}

/** "Deniz Yılmaz" -> "DY". Tek kelimeyse ilk iki harf. */
function basHarfler(ad: string): string {
  const parcalar = ad.trim().split(/\s+/).filter(Boolean);
  if (parcalar.length === 0) return "?";
  if (parcalar.length === 1) return parcalar[0]!.slice(0, 2).toLocaleUpperCase("tr-TR");
  return (parcalar[0]![0]! + parcalar[parcalar.length - 1]![0]!).toLocaleUpperCase("tr-TR");
}
